/**
 * Highlights Library SSO Integration Service
 * Handles launching Highlights Library with SSO authentication
 */

/**
 * Launches Highlights Library SSO for a user
 * @param {string} userId - The user ID to authenticate
 * @param {Object} options - Optional configuration
 * @param {number} options.ttlSeconds - Token time-to-live (60-1800 seconds)
 * @param {string} options.returnUrl - URL to return to after session
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function launchHighlightsLibrarySso(userId, options = {}) {
  let pendingWindow = null;
  try {
    const requestStartedAt = new Date().toISOString();
    console.groupCollapsed('[Highlights] SSO launch');
    pendingWindow = window.open('about:blank', '_blank');
    if (pendingWindow) {
      pendingWindow.document.title = 'Opening Highlights Library...';
      pendingWindow.document.body.innerHTML = '<p style="font-family: system-ui, sans-serif; padding: 24px;">Opening Highlights Library...</p>';
    }

    console.info('[Highlights] Provider tile clicked', {
      userId,
      ttlSeconds: options.ttlSeconds || 300,
      returnUrl: options.returnUrl || window.location.origin,
      requestStartedAt,
      pageUrl: window.location.href,
      userAgent: window.navigator.userAgent,
      openedPendingWindow: Boolean(pendingWindow),
    });

    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('REACT_APP_ADMIN_API_KEY not configured');
    }

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      throw new Error('REACT_APP_API_BASE_URL is not configured');
    }

    const normalizedBaseUrl = apiBaseUrl.replace(/\/$/, '').replace(/\/api$/, '');
    const tokenUrl = `${normalizedBaseUrl}/api/providers/highlights-library/issue-token`;

    console.info('[Highlights] Requesting launch URL', {
      tokenUrl,
      userId,
      apiBaseUrl,
      normalizedBaseUrl,
      hasAdminApiKey: Boolean(adminApiKey),
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': adminApiKey,
      },
      body: JSON.stringify({
        userId: userId,
        ttlSeconds: options.ttlSeconds || 300,
        returnUrl: options.returnUrl || window.location.origin,
      }),
    });

    const rawText = await response.text();
    let data = {};
    try {
      data = rawText ? JSON.parse(rawText) : {};
    } catch (parseError) {
      console.error('[Highlights] Failed to parse launch response JSON', {
        status: response.status,
        rawResponseLength: rawText.length,
        parseErrorName: parseError?.name,
      });
      throw new Error(`Invalid launch response JSON (HTTP ${response.status})`);
    }

    console.info('[Highlights] Launch URL response received', {
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
      rawResponseLength: rawText.length,
      responseHeaders: safeResponseHeaders(response),
      responseKeys: Object.keys(data || {}),
      hasLaunchUrl: Boolean(data.launchUrl),
      hasRedirectUrl: Boolean(data.redirectUrl),
      hasHighlightsDebug: Boolean(data.highlightsDebug),
    });

    if (!response.ok) {
      console.error('[Highlights] Launch URL request failed', {
        status: response.status,
        responseKeys: Object.keys(data || {}),
      });
      throw new Error(data.error || data.message || 'Failed to issue token');
    }

    // Open Highlights Library in new tab
    const redirectUrl = data.redirectUrl || data.launchUrl;
    if (!redirectUrl) {
      throw new Error('No redirect URL received from server');
    }

    console.info('[Highlights] Opening Highlights Library', {
      userId,
      redirectUrlLength: redirectUrl.length,
      redirectHost: safeUrlPart(redirectUrl, 'host'),
      redirectPath: safeUrlPart(redirectUrl, 'pathname'),
      redirectQueryKeys: safeQueryKeys(redirectUrl),
      redirectHighlightsParamLength: safeHighlightsParamLength(redirectUrl),
    });

    let openedWindow = pendingWindow;
    if (pendingWindow) {
      pendingWindow.location.href = redirectUrl;
      try {
        pendingWindow.opener = null;
      } catch {
        // Ignore browser restrictions here; the tab is already navigating.
      }
    } else {
      openedWindow = window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    }
    console.info('[Highlights] window.open result', {
      opened: Boolean(openedWindow),
      popupPossiblyBlocked: !openedWindow,
      usedPendingWindow: Boolean(pendingWindow),
    });
    console.groupEnd();

    return {
      success: true,
      token: data.token,
      launchUrl: data.launchUrl,
      highlightsDebug: data.highlightsDebug,
      expiresAt: data.expiresAt,
    };

  } catch (error) {
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.document.body.innerHTML = `<p style="font-family: system-ui, sans-serif; padding: 24px;">Highlights Library launch failed: ${error.message}</p>`;
    }
    console.error('[Highlights] Launch failed in browser', {
      userId,
      message: error.message,
      stack: error.stack,
      mappedMessage: getHighlightsLibraryErrorMessage(error.message),
    });
    console.groupEnd?.();
    return {
      success: false,
      error: getHighlightsLibraryErrorMessage(error.message),
      details: error,
    };
  }
}

function safeResponseHeaders(response) {
  try {
    return Array.from(response.headers.entries()).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function safeUrlPart(value, part) {
  try {
    return new URL(value)[part];
  } catch {
    return null;
  }
}

function safeQueryKeys(value) {
  try {
    return Array.from(new URL(value).searchParams.keys());
  } catch {
    return [];
  }
}

function safeHighlightsParamLength(value) {
  try {
    return new URL(value).searchParams.get('highlights')?.length || 0;
  } catch {
    return 0;
  }
}

/**
 * Check if a provider is Highlights Library
 * @param {Object} provider - Provider object from database
 * @returns {boolean} True if this is Highlights Library provider
 */
export function isHighlightsLibraryProvider(provider) {
  if (!provider) return false;

  // Check by provider key
  const normalizedKey = String(provider.key || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (normalizedKey === 'highlightslibrary' || normalizedKey === 'highlights') {
    return true;
  }

  // Check by name (fallback)
  const name = (provider.name || '').toLowerCase();
  return name.includes('highlights') && (name.includes('library') || name.includes('sso'));
}

/**
 * Get user-friendly error messages for Highlights Library SSO errors
 * @param {string} error - Error message or code
 * @returns {string} User-friendly error message
 */
export function getHighlightsLibraryErrorMessage(error) {
  const errorStr = (error || '').toLowerCase();

  // Highlights Library API error codes
  if (errorStr.includes('0001') || errorStr.includes('wrong param')) {
    return 'Configuration error with company code. Please contact support.';
  }
  if (errorStr.includes('0002')) {
    return 'Invalid user type. Please contact support.';
  }
  if (errorStr.includes('0003')) {
    return 'Invalid user information. Please contact support.';
  }
  if (errorStr.includes('0004') || errorStr.includes('not valid company_code')) {
    return 'Company not registered with Highlights Library. Please contact support.';
  }
  if (errorStr.includes('0005')) {
    return 'SSO not enabled for this company. Please contact support.';
  }
  if (errorStr.includes('0006') || errorStr.includes('0007') || errorStr.includes('duplicate')) {
    return 'Duplicate account detected. Please contact support.';
  }
  if (errorStr.includes('0010') || errorStr.includes('id length')) {
    return 'Username must be between 4-35 characters (letters and numbers only).';
  }
  if (errorStr.includes('0011') || errorStr.includes('name length')) {
    return 'Name must be between 4-35 characters.';
  }

  // Generic errors
  if (errorStr.includes('unauthorized') || errorStr.includes('api key')) {
    return 'Authentication failed. Please contact support.';
  }
  if (errorStr.includes('network') || errorStr.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  if (errorStr.includes('user_not_found')) {
    return 'User account not found. Please contact support.';
  }

  return error || 'An unexpected error occurred. Please try again.';
}
